import { enumValueGuard, IPage } from '@fmnts/core';
import { Duration as DurationType } from '@fmnts/core/chronos';
import { InlineTranslateable } from '@fmnts/i18n';
import { EntityId } from './entity';

/**
 * --------------------------------------------------------
 * @deprecated Legacy API types
 *
 * Remove this entire file, types for API should be in
 * `metrics.api-model.ts` and for the rest use shared domain models
 *
 * --------------------------------------------------------
 */

/**
 * Types that a metric can have.
 * The value of a metric is defined by the metric type.
 * @deprecated
 */
export enum MetricType {
  Average = 'average',
  /** Only used on the FE side. */
  Duration = 'duration',
  Numeric = 'numeric',
  KeyValueList = 'key-value-list',
  TextList = 'text-list',
}
/**
 * @see {@link MetricType}
 * @deprecated
 */
export const isMetricType = enumValueGuard(MetricType);

/**
 * Display type of a metric.
 *
 * This is relevant for displaying the metric.
 * @deprecated
 */
export enum MetricDisplayType {
  BarChartHorizontal = 'horizontal-bar-chart',
  BarChartVertical = 'vertical-bar-chart',
  Duration = 'duration',
  Metric = 'metric',
  Money = 'money',
  Percentage = 'percent-of',
  PieChart = 'pie-chart',
  Rating = 'rating',
  TextList = 'text-list',
}
/**
 * @see {@link MetricDisplayType}
 * @deprecated
 */
export const isMetricDisplayType = enumValueGuard(MetricDisplayType);

/** @deprecated */
export type MetricsId = EntityMetrics['id'];
/** @deprecated */
export type MetricsWidgetSlug = MetricsWidgetBase['slug'];

/** @deprecated */
export interface AverageMetric {
  /** The count of elements over which the average was aggregated. */
  count: number;
  /** The calculated average. */
  average: number;
}

/** @deprecated */
interface KeyValueMetric {
  value: number;
  key: InlineTranslateable;
}
/** @deprecated */
export type KeyValueListMetric = KeyValueMetric[];

/** @deprecated */
export interface MetricsWidgetBase<T = unknown> {
  /** Slug that identifies the metric. */
  slug: string;
  /** Describes the type of the metric. */
  metricType: `${MetricType}`;
  /** How the metric should be displayed. */
  displayType: `${MetricDisplayType}`;
  /** Title to show. */
  title: InlineTranslateable;
  /** Value for the current period. */
  value: T;
  /** Whether this metric can be used for sorting. */
  isSortable?: boolean;
  /** Custom CSS classes. */
  cssClasses?: string;
  /** Icon slug from fontawesome (without the prefix fa-). */
  icon?: string;
}

/**
 * A data structure that a metrics widget for each metric
 * by the slug of that same metric.
 * @deprecated
 */
export interface MetricWidgetsBySlug<T> {
  [slug: MetricsWidgetSlug]: T;
}

/**
 * A data structure that a metrics widget for each metric
 * by the slug of that same metric.
 * @deprecated
 */
export interface MetricWidgetMap<
  T extends Record<string, MetricsWidgetBase<unknown>>,
> {
  /** Contains an ordered list of slugs. */
  slugs: MetricsWidgetSlug[];
  /**
   * Contains the metrics.
   */
  bySlug: T;
}

/**
 * Contains metrics for a singly domain entity.
 * @deprecated
 */
export interface EntityMetrics {
  /** ID of the entity. */
  id: EntityId;
  /** Display name of the entity. */
  name: string;
  /** Image of the resource */
  image: string | null;
  /** Widgets per slug. */
  widgets: MetricWidgetMap<MetricWidgetsBySlug<MetricsWidget>>;
  /** Additional code to display as a badge */
  code?: string;
}

/**
 * A metrics page contains a list of results.
 * @deprecated
 */
export interface MetricsPage<
  T,
  TMetricTotals extends MetricWidgetsBySlug<MetricsWidgetBase<unknown>> =
    MetricWidgetsBySlug<MetricsWidget>,
> extends IPage<T> {
  /**
   * Accumulated totals for the widgets.
   * May be `undefined` in cases where totals don't make sense,
   * e.g. when fetching metrics for a single entity.
   */
  totals: MetricWidgetMap<TMetricTotals>;
}

export interface AverageMetricsWidget extends MetricsWidgetBase<AverageMetric> {
  metricType: `${MetricType.Average}`;
}
export interface DurationMetricsWidget extends MetricsWidgetBase<DurationType> {
  metricType: `${MetricType.Duration}`;
}
export interface NumericMetricsWidget extends MetricsWidgetBase<number> {
  metricType: `${MetricType.Numeric}`;
}
export interface KeyValueListMetricsWidget extends MetricsWidgetBase<KeyValueListMetric> {
  metricType: `${MetricType.KeyValueList}`;
}
export interface TextListMetricsWidget extends MetricsWidgetBase<
  InlineTranslateable[]
> {
  metricType: `${MetricType.TextList}`;
}

// Key Value List metric
interface MetricsWidgetBarChartHorizontal extends KeyValueListMetricsWidget {
  displayType: `${MetricDisplayType.BarChartHorizontal}`;
}
interface MetricsWidgetBarChartVertical extends KeyValueListMetricsWidget {
  displayType: `${MetricDisplayType.BarChartVertical}`;
}
interface MetricsWidgetPieChart extends KeyValueListMetricsWidget {
  displayType: `${MetricDisplayType.PieChart}`;
}
interface MetricsWidgetTextValueList extends KeyValueListMetricsWidget {
  displayType: `${MetricDisplayType.TextList}`;
}

// Duration Metric
interface MetricsWidgetDuration extends DurationMetricsWidget {
  displayType: `${MetricDisplayType.Duration}`;
}

// Numeric Metric
interface MetricsWidgetMetric extends NumericMetricsWidget {
  displayType: `${MetricDisplayType.Metric}`;
}
interface MetricsWidgetMoney extends NumericMetricsWidget {
  displayType: `${MetricDisplayType.Money}`;
}
interface MetricsWidgetPercentage extends NumericMetricsWidget {
  displayType: `${MetricDisplayType.Percentage}`;
}

// Average Metric

interface MetricsWidgetAverageMetric extends AverageMetricsWidget {
  displayType: `${MetricDisplayType.Metric}`;
}
interface MetricsWidgetAverageMoney extends AverageMetricsWidget {
  displayType: `${MetricDisplayType.Money}`;
}
interface MetricsWidgetAveragePercentage extends AverageMetricsWidget {
  displayType: `${MetricDisplayType.Percentage}`;
}
interface MetricsWidgetAverageRating extends AverageMetricsWidget {
  displayType: `${MetricDisplayType.Rating}`;
}

// Text List
interface MetricsWidgetTextList extends TextListMetricsWidget {
  displayType: `${MetricDisplayType.TextList}`;
}

/** @deprecated */
export type MetricsWidget =
  | MetricsWidgetBarChartHorizontal
  | MetricsWidgetBarChartVertical
  | MetricsWidgetPieChart
  | MetricsWidgetTextValueList
  | MetricsWidgetDuration
  | MetricsWidgetMetric
  | MetricsWidgetMoney
  | MetricsWidgetPercentage
  | MetricsWidgetAverageMetric
  | MetricsWidgetAverageMoney
  | MetricsWidgetAveragePercentage
  | MetricsWidgetAverageRating
  | MetricsWidgetTextList;
